import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProfileTemplate from './ui/ProfileTemplate';
import './ui/profilepage.css';
import { BASE_URL } from '../Components/Constant';

const API_BASE = `${BASE_URL}/consultant_profiles`;
// Derived from API_BASE so there's a single source of truth for the
// backend host — used to resolve PhotoUrl to an absolute URL below.
const BACKEND_ORIGIN = new URL(API_BASE).origin;

function toCamel(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      const key = k === '_id' ? 'id' : k.charAt(0).toLowerCase() + k.slice(1);
      return [key, v]; // values passed through untouched — no recursion
    })
  );
}

// If photoUrl is already absolute (http/https) or a data: URL, leave it
// alone. If it's a bare relative path (older records saved before the
// backend started returning absolute URLs, or if it ever regresses to one),
// prefix it with the backend's own origin so <img src={photoUrl}> resolves
// against the Rails server that actually serves the file, not whatever host
// is currently displaying this page.
function resolvePhotoUrl(photoUrl) {
  if (!photoUrl) return photoUrl;
  if (/^https?:\/\//i.test(photoUrl) || photoUrl.startsWith('data:')) return photoUrl;
  const path = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;
  return `${BACKEND_ORIGIN}${path}`;
}

function publicView(rec) {
  if (!rec?.anonymize) return rec;
  const parts = (rec.name || '').trim().split(/\s+/).filter(Boolean);
  const displayName = parts.length
    ? parts[0] + (parts[1] ? ` ${parts[1][0]}.` : '')
    : 'Aptclouds Consultant';
  return { ...rec, name: displayName, email: '', phone: '' };
}

export default function ProfilePage() {
  const { id } = useParams(); // expects a route like /p/:id

  const [loadState, setLoadState] = useState('loading'); // loading | found | notfound | error
  const [rec, setRec] = useState(null);

  const [gateOpen, setGateOpen] = useState(true);
  const [gateStep, setGateStep] = useState('email'); // 'email' | 'otp'
  const [eligible, setEligible] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [gateError, setGateError] = useState('');
  const [checking, setChecking] = useState(false);
  const [resendNotice, setResendNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/${id}`)
      .then(async (res) => {
        if (res.status === 404) return { __notfound: true };
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return toCamel(await res.json());
      })
      .then((data) => {
        if (cancelled) return;
        if (data.__notfound) { setLoadState('notfound'); return; }
        setRec({ ...data, photoUrl: resolvePhotoUrl(data.photoUrl) });
        setLoadState('found');
      })
      .catch(() => { if (!cancelled) setLoadState('error'); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (loadState === 'found' && rec) {
      document.title = `${rec.name || 'Consultant'} — ${rec.title || ''} | Aptclouds`;
    }
  }, [loadState, rec]);

  async function requestOtp(e) {
    e.preventDefault();
    setChecking(true);
    setGateError('');
    setResendNotice('');
    try {
      const res = await fetch(`${API_BASE}/${id}/request_otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = await res.json();
      if (!res.ok || body.success === false) {
        setGateError(body.error || "This profile hasn't been shared with that email address.");
        return;
      }
      if (body.otp_required === false) {
        setEligible(true);
        setGateOpen(false);
        return;
      }
      setGateStep('otp');
    } catch {
      setGateError('Something went wrong — please try again.');
    } finally {
      setChecking(false);
    }
  }

  async function verifyOtp(e) {
    e.preventDefault();
    setChecking(true);
    setGateError('');
    try {
      const res = await fetch(`${API_BASE}/${id}/verify_otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), otp: otp.trim() }),
      });
      const body = await res.json();
      if (!res.ok || body.success === false) {
        setGateError(body.error || 'Incorrect code');
        return;
      }
      setEligible(true);
      setGateOpen(false);
    } catch {
      setGateError('Something went wrong — please try again.');
    } finally {
      setChecking(false);
    }
  }

  async function resendOtp() {
    setChecking(true);
    setGateError('');
    setResendNotice('');
    try {
      const res = await fetch(`${API_BASE}/${id}/request_otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = await res.json();
      if (!res.ok || body.success === false) {
        setGateError(body.error || 'Could not resend the code — please try again.');
        return;
      }
      setOtp('');
      setResendNotice('A new code has been sent.');
    } catch {
      setGateError('Something went wrong — please try again.');
    } finally {
      setChecking(false);
    }
  }

  function backToEmail() {
    setGateStep('email');
    setOtp('');
    setGateError('');
    setResendNotice('');
  }

  function copyLink() {
    navigator.clipboard?.writeText(window.location.href);
  }

  return (
    <div className="consultant-profile-page">

      {loadState === 'loading' && <div className="empty">Loading…</div>}

      {loadState === 'notfound' && (
        <div className="empty"><div>Profile not found.</div></div>
      )}

      {loadState === 'error' && (
        <div className="empty"><div>Couldn't load this profile. Please try again shortly.</div></div>
      )}

      {loadState === 'found' && (
        <>
          {gateOpen && gateStep === 'email' && (
            <div className="overlay overlay-center">
              <div className="dialog" role="dialog" aria-modal="true">
                <h2 className="dialog-title">This profile is shared privately</h2>
                <p className="dialog-msg">Enter the email address this profile was shared with to view it.</p>
                <form onSubmit={requestOtp}>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>Email address</label>
                    <input
                      className="input"
                      type="email"
                      required
                      autoFocus
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {gateError && <div className="hint" style={{ color: '#c0392b' }}>{gateError}</div>}
                  </div>
                  <div className="dialog-foot">
                    <button type="submit" className="btn btn-primary" disabled={checking}>
                      {checking ? 'Checking…' : 'Continue'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {gateOpen && gateStep === 'otp' && (
            <div className="overlay overlay-center">
              <div className="dialog" role="dialog" aria-modal="true">
                <h2 className="dialog-title">Check your email</h2>
                <p className="dialog-msg">
                  We sent a 6-digit code to <strong>{email}</strong>. Enter it below to view the profile.
                </p>
                <form onSubmit={verifyOtp}>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>Verification code</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      required
                      autoFocus
                      placeholder="123456"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    />
                    {gateError && <div className="hint" style={{ color: '#c0392b' }}>{gateError}</div>}
                    {resendNotice && <div className="hint">{resendNotice}</div>}
                  </div>
                  <div className="dialog-foot" style={{ justifyContent: 'space-between' }}>
                    <button type="button" className="btn btn-light" onClick={backToEmail} disabled={checking}>
                      Use a different email
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={checking}>
                      {checking ? 'Verifying…' : 'View Profile'}
                    </button>
                  </div>
                </form>
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={checking}
                  className="hint"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', marginTop: 10, padding: 0 }}
                >
                  Didn't get a code? Resend
                </button>
              </div>
            </div>
          )}

          {eligible && (
            <main className="page">
              <div className="pub-actions no-print">
                <button onClick={copyLink}>Copy link</button>
                <button onClick={() => window.print()}>Download PDF</button>
              </div>
              <ProfileTemplate rec={publicView(rec)} />
              <div className="confidential">
                {rec.anonymize
                  ? 'Confidential profile shared by Aptclouds. Please do not redistribute.'
                  : 'Profile presented by Aptclouds — senior cloud consultants, AI-accelerated delivery.'}
              </div>
            </main>
          )}
        </>
      )}

    </div>
  );
}